import React from "react";

interface DataPoint {
  feature: string;
  item1: string;
  item2: string;
  icon: string;
  description: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  data?: string | DataPoint[];
  bgColor?: string;
  item1Label?: string;
  item2Label?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultData: DataPoint[] = [
  {
    feature: "",
    item1: "",
    item2: "",
    icon: "⭐",
    description: "",
  },
  {
    feature: "",
    item1: "",
    item2: "",
    icon: "⭐",
    description: "",
  },
  {
    feature: "",
    item1: "",
    item2: "",
    icon: "⭐",
    description: "",
  },
  {
    feature: "",
    item1: "",
    item2: "",
    icon: "⭐",
    description: "",
  },
];

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const ComparisonSlide1 = {
  id: "comparison-slide1",
  name: "Comparison Style 1",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "item1Label",
      label: "Item 1 Label",
      type: "text",
      defaultValue: "",
    },
    {
      id: "item2Label",
      label: "Item 2 Label",
      type: "text",
      defaultValue: "",
    },
    {
      id: "data",
      label: "Comparison Data",
      type: "text",
      defaultValue: JSON.stringify(defaultData),
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "",
      subtitle = "",
      bgColor = "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
      data,
      item1Label = "",
      item2Label = "",
      onEdit = () => {},
    } = content;

    // Fix data parsing
    let parsedData: DataPoint[] = defaultData;
    try {
      if (typeof data === "string") {
        parsedData = JSON.parse(data);
      } else if (Array.isArray(data)) {
        parsedData = data;
      }
    } catch (error) {
      console.warn("Error parsing data:", error);
      console.warn("Raw data:", data);
    }

    // Ensure each item has all required properties
    parsedData = parsedData.map((item) => ({
      feature: item.feature || "",
      item1: item.item1 || "",
      item2: item.item2 || "",
      icon: item.icon || "⭐",
      description: item.description || "",
    }));

    const handleDataEdit = (index: number, field: string, value: string) => {
      const updatedData = [...parsedData];
      updatedData[index] = {
        ...updatedData[index],
        [field]: value,
      };
      onEdit({ ...content, data: JSON.stringify(updatedData) });
    };

    // Calculate dynamic sizes based on number of items
    const itemCount = parsedData.length;
    const getRowCount = () => {
      if (itemCount <= 6) return 3;
      if (itemCount <= 8) return 4;
      return Math.ceil(itemCount / 2);
    };

    const getHeaderHeight = () => {
      if (itemCount <= 6) return "h-24";
      if (itemCount <= 8) return "h-20";
      return "h-16";
    };

    const getCardHeight = () => {
      const rowCount = getRowCount();
      if (rowCount <= 3) return "h-32";
      if (rowCount <= 4) return "h-24";
      return "h-20";
    };

    const getFontSizes = () => {
      if (itemCount <= 6) {
        return {
          header: "text-2xl",
          subheader: "text-xl",
          label: "text-lg",
          feature: "text-base",
          description: "text-sm",
        };
      }
      if (itemCount <= 8) {
        return {
          header: "text-xl",
          subheader: "text-lg",
          label: "text-base",
          feature: "text-sm",
          description: "text-xs",
        };
      }
      return {
        header: "text-lg",
        subheader: "text-base",
        label: "text-sm",
        feature: "text-xs",
        description: "text-xs",
      };
    };

    const sizes = getFontSizes();
    const headerHeight = getHeaderHeight();
    const cardHeight = getCardHeight();

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full p-2">
          {/* Header */}
          <div
            className={`text-center ${headerHeight} flex flex-col justify-center`}
          >
            <EditableDiv
              text={title}
              onEdit={(value) => onEdit({ ...content, title: value })}
              className={`${sizes.header} font-bold mb-1 bg-clip-text text-transparent bg-gradient-to-r from-blue-200 to-blue-100`}
            />
            <EditableDiv
              text={subtitle}
              onEdit={(value) => onEdit({ ...content, subtitle: value })}
              className={`${sizes.subheader} text-blue-100/90`}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 grid grid-cols-2 gap-2">
            {/* Left Column - Sivas Kongresi */}
            <div className="flex flex-col">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 mb-2 text-center">
                <EditableDiv
                  text={item1Label}
                  onEdit={(value) => onEdit({ ...content, item1Label: value })}
                  className={`${sizes.label} font-semibold text-blue-200`}
                />
              </div>
              <div
                className="flex-1 grid gap-2"
                style={{
                  gridTemplateRows: `repeat(${getRowCount()}, minmax(0, 1fr))`,
                }}
              >
                {parsedData.map((item, index) => (
                  <div
                    key={index}
                    className={`bg-white/10 backdrop-blur-sm rounded-lg p-2 transform transition-all duration-300 hover:bg-white/15 flex flex-col justify-between`}
                  >
                    <div className="flex items-center gap-1">
                      <span className={sizes.feature}>{item.icon}</span>
                      <div className="flex-1 min-w-0">
                        <EditableDiv
                          text={item.feature}
                          onEdit={(value) =>
                            handleDataEdit(index, "feature", value)
                          }
                          className={`font-semibold ${sizes.feature} text-blue-100 truncate`}
                        />
                        <EditableDiv
                          text={item.description}
                          onEdit={(value) =>
                            handleDataEdit(index, "description", value)
                          }
                          className={`${sizes.description} text-blue-200/80 truncate`}
                        />
                      </div>
                    </div>
                    <div className="bg-white/5 rounded p-1.5 mt-1 transition-all duration-300 hover:bg-white/10">
                      <EditableDiv
                        text={item.item1}
                        onEdit={(value) =>
                          handleDataEdit(index, "item1", value)
                        }
                        className={`${sizes.feature} font-medium text-center truncate`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - TBMM */}
            <div className="flex flex-col">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-2 mb-2 text-center">
                <EditableDiv
                  text={item2Label}
                  onEdit={(value) => onEdit({ ...content, item2Label: value })}
                  className={`${sizes.label} font-semibold text-blue-200`}
                />
              </div>
              <div
                className="flex-1 grid gap-2"
                style={{
                  gridTemplateRows: `repeat(${getRowCount()}, minmax(0, 1fr))`,
                }}
              >
                {parsedData.map((item, index) => (
                  <div
                    key={index}
                    className={`bg-white/10 backdrop-blur-sm rounded-lg p-2 transform transition-all duration-300 hover:bg-white/15 flex flex-col justify-between`}
                  >
                    <div className="flex items-center gap-1">
                      <span className={sizes.feature}>{item.icon}</span>
                      <div className="flex-1 min-w-0">
                        <EditableDiv
                          text={item.feature}
                          onEdit={(value) =>
                            handleDataEdit(index, "feature", value)
                          }
                          className={`font-semibold ${sizes.feature} text-blue-100 truncate`}
                        />
                        <EditableDiv
                          text={item.description}
                          onEdit={(value) =>
                            handleDataEdit(index, "description", value)
                          }
                          className={`${sizes.description} text-blue-200/80 truncate`}
                        />
                      </div>
                    </div>
                    <div className="bg-white/5 rounded p-1.5 mt-1 transition-all duration-300 hover:bg-white/10">
                      <EditableDiv
                        text={item.item2}
                        onEdit={(value) =>
                          handleDataEdit(index, "item2", value)
                        }
                        className={`${sizes.feature} font-medium text-center truncate`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
};

export default ComparisonSlide1;
